import React from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';

import { getApplicantsId } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { Applicant } from '../api/generated/model';
import ApplicantDetailContent from './ApplicantDetailContent';
import { CopyButton } from './common/Copyable';

interface Props {
  show: boolean;
  onHide: () => void;
  applicant?: Applicant | null;
  applicantId?: string | null;
  onChanged?: (a: Applicant) => void;
  onDeleted?: () => void;
}

const ApplicantDetailModal: React.FC<Props> = ({ show, onHide, applicant: applicantProp, applicantId, onChanged, onDeleted }) => {
  const { data: fetched, isLoading } = useQuery({
    queryKey: ['applicant', applicantId],
    queryFn: () => getApplicantsId(applicantId!),
    enabled: show && !!applicantId && !applicantProp,
  });

  const applicant = applicantProp || fetched || null;

  if (!show) return null;

  if (isLoading && !applicant) {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" className="me-2" />
          Loading applicant...
        </Modal.Body>
      </Modal>
    );
  }

  if (!applicant) return null;

  const name = [applicant.first_name, applicant.last_name].filter(Boolean).join(' ') || applicant.email;

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-3 flex-wrap">
          <span>{name}</span>
          {applicant.id && (
            <div className="d-flex gap-2 bg-header-control rounded px-2 py-1">
              <a
                href={`/applicants/${applicant.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary"
              >
                <i className="bi bi-box-arrow-up-right me-1"></i>
                Open
              </a>
              <CopyButton
                value={`${window.location.origin}/applicants/${applicant.id}`}
                variant="outline-primary"
                size="sm"
                title="Copy link to clipboard"
              >
                <i className="bi bi-link-45deg me-1"></i>
                Copy Link
              </CopyButton>
            </div>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3">
        <ApplicantDetailContent applicant={applicant} onChanged={onChanged} onDeleted={onDeleted} />
      </Modal.Body>
    </Modal>
  );
};

export default ApplicantDetailModal;
